import { useState, useEffect, useCallback, useMemo } from 'react';
import Toast, { useToast } from '../../components/Toast';
import { useNavigate } from 'react-router-dom';
import { supabase, splitFullName } from '../../lib/supabase';
import { updateCaregiver } from '../../lib/axiscare';
import { useAuth } from '../../hooks/useAuth';
import { notifyOfferLetterReady, notifyOfferLetterReminder, notifyPlacementAcknowledgmentReminder, notifyApplicantInvite, notifyApplicationRejected, notifyApplicationDocumentsMissing } from '../../lib/notifications';
import { format } from 'date-fns';
import { ROLES, getRole, getOfferLetterHtml, getJobDescriptionHtml, TEMPLATE_CODE_TO_POSITION, BACKGROUND_CHECK_CONSENT, BUNDLE_DOC_TYPES, POLICY_ACKNOWLEDGMENT, CONFIDENTIALITY_AGREEMENT, ORIENTATION_ATTESTATION, getOrientationAttestationHtml } from '../../lib/onboardingTemplates';
import { bgCheckTypeFor, provisionalExpiry, daysUntil, startGchexsCheck, clearBackgroundCheck as clearBgCheckShared, flagBackgroundCheck as flagBgCheckShared, markLicenseVerified as markLicenseVerifiedShared, recordScreeningCheck, SCREENING_KINDS } from '../../lib/staffCompliance';
import { BgCheckBadge, ClinicalCertsPanel } from '../../components/hr/StaffCompliancePanels';
import ReviseOfferModal from '../../components/hr/ReviseOfferModal';
import { credentialDateFlags, flagMessage } from '../../lib/credentialDates';
import { APPLICATION_INCOMPLETE, APPLICATION_RECEIVED, APPLICATION_OPEN_HOURS, missingDocuments, licenseRequired } from '../../lib/applicationDocuments';

// 2026-09-07 — one-email onboarding. The board has two working lanes:
// Candidate (documents on file? prepare the bundle) and Offer Out (waiting
// on one signature). Signing the bundle is the ONLY gate: it makes the
// person active, creates their AxisCare account, issues their training link
// and sends one welcome email (see submit-staff-signatures). Everything that
// used to be a lane after that — Gusto confirmation, the AxisCare click, LMS
// completion, the office-filled orientation checklist, "Complete
// onboarding" — is now a dated follow-up on Training & Compliance. Unsigned
// bundles expire on their accept-by date (scheduled-notifications
// onboarding_followups) and drop off the board as Withdrawn.
// Mirrors the `roles` table's labels — small/stable enough for a plain
// display lookup here rather than an async fetch just for a kanban caption.
const POSITION_LABELS = { RN: 'Registered Nurse', LPN: 'Licensed Practical Nurse', PCA: 'Personal Care Assistant', PCC: 'Patient Care Coordinator', DIR_OPS: 'Director of Operations', BOC: 'Business Operations Coordinator', MGR: 'Manager' };


const LANES = [
  { key: 'candidate',      label: 'Candidate', color: 'var(--zone-leads)' },
  { key: 'offer_sent',     label: 'Offer Out', color: 'var(--zone-marketing)' },
  // v56 — agency placements sign but do not auto-activate. Their file was
  // assembled by their employer, so somebody here looks at it before they
  // reach a client, and that approval is what countersigns the document.
  { key: 'pending_review', label: 'Awaiting Review', color: 'var(--zone-approval)' },
];

// Every candidate must have these on file before a bundle can go out
// (2026-09-07). They come from the application (staff_applications.*_url)
// or, for hand-added candidates, from staff_documents rows the office
// uploads in the candidate modal. License/certificate: RN, LPN, CNA only.
const REQUIRED_DOCS = [
  { key: 'resume',       label: 'Resume',                            appField: 'resume_url' },
  { key: 'id_document',  label: "Driver's license / government ID",  appField: 'id_document_url' },
  { key: 'cpr_card',     label: 'CPR / First Aid card',              appField: 'cpr_card_url',     certType: 'cpr_first_aid', certLabel: 'CPR / First Aid',  dateField: 'cpr_expiry_date',     dateKind: 'expiry' },
  { key: 'tb_result',    label: 'TB test result',                    appField: 'tb_result_url',    certType: 'tb_screening',  certLabel: 'TB Screening',     dateField: 'tb_test_date',        dateKind: 'issued', validMonths: 12 },
  { key: 'license_cert', label: 'Nursing license / certificate',     appField: 'license_cert_url', certType: 'rn_lpn_licence', certLabel: 'RN/LPN Licence',  dateField: 'license_expiry_date', dateKind: 'expiry', licenseOnly: true },
];
// Applicants before this date were never asked for CPR/TB/SSN, so for them
// the missing-document check is a warning, not a block. Everyone created
// from this date on (public form or Quick Add) must have the set on file.
const DOCUMENT_GATE_SINCE = '2026-09-07';
const isLegacyCandidate = (staff) => !!staff?.created_at && staff.created_at.slice(0, 10) < DOCUMENT_GATE_SINCE;
// Certification dates: an expiry as given, or issued date + validity (TB).
const certDatesFor = (doc, dateValue) => {
  if (!dateValue) return {};
  if (doc.dateKind === 'expiry') return { expiry_date: dateValue };
  const d = new Date(`${dateValue}T00:00:00`); d.setMonth(d.getMonth() + (doc.validMonths || 12));
  return { issued_date: dateValue, expiry_date: d.toISOString().slice(0, 10) };
};
const licenseRequiredFor = (positionCode, roleCode) =>
  ['RN', 'LPN'].includes(positionCode) || ['RN', 'LPN', 'LEAD_RN', 'PCA_CNA'].includes(roleCode);

const TRACKS = [
  { key: 'employee',   label: 'Employee (W2)',       hint: 'Offer letter · Gusto payroll' },
  { key: 'contractor', label: 'Contractor (1099)',   hint: 'Contractor letter · Gusto for W-9' },
  { key: 'agency',     label: 'Agency nurse',        hint: 'No offer letter, no Gusto — the sending agency employs them (Policy A-2.4)' },
];

// Bundle document content (BACKGROUND_CHECK_CONSENT, POLICY_ACKNOWLEDGMENT,
// CONFIDENTIALITY_AGREEMENT, AGENCY_WORKER_ACKNOWLEDGMENT, the orientation
// attestation) lives in onboardingTemplates.js — shared with the retroactive
// "Send request" actions in PersonnelProfile.js's Personnel File checklist.


// bgCheckTypeFor/PROVISIONAL_DAYS/provisionalExpiry/daysUntil now live in
// src/lib/staffCompliance.js (2026-08-08) — shared with PersonnelProfile.js's
// self-contained BackgroundCheckPanel.

function ModalBackdrop({ onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--card-bg)', borderRadius: 14, width: '100%', maxWidth: 520, boxShadow: '0 24px 48px rgba(0,0,0,0.25)', flexShrink: 0 }}>
        {children}
      </div>
    </div>
  );
}

function LaneHeader({ label, color, count }) {
  return (
    <div style={{ padding: '12px 14px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color }}>{label}</span>
      <span style={{ fontSize: 11, fontWeight: 700, background: color, color: '#fff', width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{count}</span>
    </div>
  );
}

function EmptyLane({ color }) {
  return <div style={{ textAlign: 'center', padding: '24px 10px', color, fontSize: 12, opacity: 0.5 }}>Empty</div>;
}


// The whole bundle is prepared here in one go (2026-09-07): hire track,
// role, pay, dates, then a live preview of every document the candidate
// will sign under one link. Nothing can be sent while a required document
// is missing — the office either uploads it (received by email/text) or
// asks the candidate for it from this same panel.
const fieldLabel = { fontSize: 11, color: 'var(--text3)', marginBottom: 3 };
const fieldWrap = { marginBottom: 8 };

// Rendered like an actual letter (fixed white/black, not theme tokens) —
// deliberate, since this is a preview of what the candidate will see on
// HRDocSign.js's own plain white page. Toggles to a raw-HTML textarea only
// when the office needs to hand-tweak wording.
function DocPreview({ title, html, editing, onToggleEdit, onChangeHtml }) {
  return (
    <div style={fieldWrap}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)' }}>{title}</div>
        <button type="button" onClick={onToggleEdit} style={{ background: 'none', border: 'none', color: 'var(--accent2)', fontSize: 11, cursor: 'pointer', padding: 0 }}>
          {editing ? '✓ Done editing' : '✎ Edit wording'}
        </button>
      </div>
      {editing ? (
        <textarea value={html} onChange={e => onChangeHtml(e.target.value)} rows={10} style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 11 }} />
      ) : (
        <div
          style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '16px 18px', maxHeight: 220, overflowY: 'auto', background: '#fff', color: '#222', fontSize: 13, lineHeight: 1.6 }}
          dangerouslySetInnerHTML={{ __html: html || '<p style="color:#999">Select a role above to generate this document.</p>' }}
        />
      )}
    </div>
  );
}

function OfferPanel({ staff, agencies, busy, onSend, officeName, missingFor, dateFlags, docsLoaded, docActionBusy, onRequestDocs, onUploadDoc }) {
  const initialTrack = ['employee', 'contractor', 'agency'].includes(staff.hire_track || staff.employment_type) ? (staff.hire_track || staff.employment_type) : '';
  const [track, setTrack] = useState(initialTrack);
  const [roleCode, setRoleCode] = useState(staff.hire_role_code || (staff.position_code === 'RN' ? 'RN' : staff.position_code === 'LPN' ? 'LPN' : ''));
  const [contractingAgencyId, setContractingAgencyId] = useState(staff.contracting_agency_id || '');
  const [lang, setLang] = useState(staff.preferred_language === 'es' ? 'es' : 'en');
  const [candidateName, setCandidateName] = useState(staff.full_name || '');
  const [payRate, setPayRate] = useState(staff.pay_rate != null ? String(staff.pay_rate) : '');
  const [startDate, setStartDate] = useState(staff.start_date ? staff.start_date.slice(0, 10) : '');
  const [acceptByDate, setAcceptByDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString().slice(0, 10); });
  const [alreadyWorking, setAlreadyWorking] = useState(!!staff.fast_tracked_at);
  const [overrides, setOverrides] = useState({});
  const [editing, setEditing] = useState({});

  const role = getRole(roleCode);
  const isAgency = track === 'agency';
  const offerTemplateExists = !!(role && !isAgency && track && role.offerLetters?.[track]);
  const payLabel = isAgency ? 'Agency Bill Rate ($ / hour)' : role?.payUnit === 'weekly' ? 'Weekly Service Fee ($)' : 'Pay Rate ($ / hour)';
  const selectedAgency = agencies.find(a => a.id === contractingAgencyId);

  const startDateStr = startDate ? format(new Date(`${startDate}T00:00:00`), 'MMMM d, yyyy') : 'a mutually agreed date';
  const acceptByStr = acceptByDate ? format(new Date(`${acceptByDate}T00:00:00`), 'MMMM d, yyyy') : '';

  // Generated bundle, in signing order. Overrides (hand edits) reset when
  // any underlying input changes so a stale edit never survives a correction.
  const generated = useMemo(() => {
    if (!role || !track) return [];
    const docs = [];
    // Agency-placed people sign ONE document — the Placement Acknowledgment &
    // GAPP Policy Attestation — and it is built by the agency-request-signature
    // edge function so the office route and the agency portal produce the same
    // document. Nothing is generated here for them, deliberately: the previous
    // five-document bundle (offer-flavoured acknowledgments, a Personnel
    // Handbook acknowledgment, a background-check consent) asked a nurse
    // employed by someone else to sign employment paperwork, which is what the
    // agency objected to and what the law does not require. See v56.
    if (isAgency) return [];
    {
      if (offerTemplateExists) {
        docs.push({ doc_type: 'offer_letter', doc_label: 'Offer Letter', content: getOfferLetterHtml(roleCode, track, { full_name: candidateName, pay_rate: payRate }, { startDateStr, acceptByStr }, lang) || '' });
      }
      const jd = getJobDescriptionHtml(roleCode, lang);
      if (jd) docs.push({ doc_type: 'job_description', doc_label: 'Job Description', content: jd });
    }
    const consent = BACKGROUND_CHECK_CONSENT[lang] || BACKGROUND_CHECK_CONSENT.en;
    docs.push({ doc_type: 'background_check_consent', doc_label: consent.doc_label, content: consent.content });
    const policy = POLICY_ACKNOWLEDGMENT[lang] || POLICY_ACKNOWLEDGMENT.en;
    docs.push({ doc_type: 'policy_acknowledgment', doc_label: policy.doc_label, content: policy.content });
    const conf = CONFIDENTIALITY_AGREEMENT[lang] || CONFIDENTIALITY_AGREEMENT.en;
    docs.push({ doc_type: 'confidentiality_agreement', doc_label: conf.doc_label, content: conf.content });
    const att = ORIENTATION_ATTESTATION[lang] || ORIENTATION_ATTESTATION.en;
    docs.push({ doc_type: 'orientation_attestation', doc_label: att.doc_label, content: getOrientationAttestationHtml(lang) });
    return docs;
    // selectedAgency dropped from the deps in v56 — the agency branch returns
    // early now and no longer interpolates the agency name into a document.
  }, [role, track, isAgency, offerTemplateExists, roleCode, lang, candidateName, payRate, startDateStr, acceptByStr]);
  useEffect(() => { setOverrides({}); setEditing({}); }, [roleCode, track, candidateName, payRate, startDate, acceptByDate, lang, contractingAgencyId]);

  const docs = generated.map(d => ({ ...d, content: overrides[d.doc_type] ?? d.content }));
  const missing = missingFor(roleCode);
  const legacy = isLegacyCandidate(staff);
  // W2 and 1099 hires never go out without an offer letter, a pay rate and a
  // start date (2026-09-28). Before this, a missing template quietly made all
  // three optional and the bundle went anyway — which is how two W2 LPNs were
  // activated with no signed offer letter and no pay rate on file.
  const detailsOk = isAgency
    ? !!contractingAgencyId && candidateName.trim()
    : candidateName.trim() && offerTemplateExists && payRate !== '' && !!startDate;
  // Agency placements have no locally-built bundle to count, so document
  // presence isn't the gate for them — role, agency and a complete file are.
  const canSend = role && track && (isAgency || docs.length > 0) && detailsOk && docsLoaded && (legacy || missing.length === 0);

  const trackBtn = (t) => ({
    flex: 1, textAlign: 'left', padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
    border: `1px solid ${track === t.key ? 'var(--accent)' : 'var(--border)'}`,
    background: track === t.key ? 'var(--accent-bg)' : 'transparent', color: 'var(--text)',
  });

  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>
        One bundle, one signature. Signing makes {staff.first_name || 'them'} active, creates their AxisCare account and sends a single welcome email. Everything else becomes a follow-up on Training &amp; Compliance.
      </div>

      <div style={fieldLabel}>Hire track</div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        {TRACKS.map(t => (
          <button key={t.key} type="button" onClick={() => setTrack(t.key)} style={trackBtn(t)}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>{t.label}</div>
            <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2, lineHeight: 1.3 }}>{t.hint}</div>
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <div>
          <div style={fieldLabel}>Role</div>
          <select value={roleCode} onChange={e => setRoleCode(e.target.value)} style={inputStyle}>
            <option value="">Select role…</option>
            {ROLES.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
          </select>
        </div>
        {isAgency ? (
          <div>
            <div style={fieldLabel}>Contracting Agency</div>
            <select value={contractingAgencyId} onChange={e => setContractingAgencyId(e.target.value)} style={inputStyle}>
              <option value="">Select agency…</option>
              {agencies.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            {selectedAgency && !selectedAgency.signed_pdf_url && (
              <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 4 }}>⚠ No signed A-2.4 agreement on file for this agency.</div>
            )}
          </div>
        ) : (
          <div>
            <div style={fieldLabel}>Document Language</div>
            <LangToggle lang={lang} setLang={setLang} />
          </div>
        )}
      </div>
      {isAgency && (
        <div style={{ marginBottom: 8 }}>
          <div style={fieldLabel}>Document Language</div>
          <LangToggle lang={lang} setLang={setLang} />
        </div>
      )}

      {role && track && !isAgency && !offerTemplateExists && (
        <div style={{ fontSize: 11, color: 'var(--danger)', background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
          There is no {track === 'employee' ? 'W2' : '1099'} offer letter for {role.label}, so this bundle can't be sent. Every role needs both a W2 and a 1099 letter in onboardingTemplates.js.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <div>
          <div style={fieldLabel}>Candidate Name</div>
          <input value={candidateName} onChange={e => setCandidateName(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <div style={fieldLabel}>{payLabel}{isAgency ? ' (optional)' : ''}</div>
          <input type="number" step="0.01" min="0" placeholder="e.g. 20.00" value={payRate} onChange={e => setPayRate(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <div style={fieldLabel}>Start Date{isAgency ? ' (optional)' : ''}</div>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <div style={fieldLabel}>Sign By (offer withdraws after)</div>
          <input type="date" value={acceptByDate} onChange={e => setAcceptByDate(e.target.value)} style={inputStyle} />
        </div>
      </div>

      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, color: 'var(--text2)', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', marginBottom: 10, cursor: 'pointer' }}>
        <input type="checkbox" checked={alreadyWorking} onChange={e => setAlreadyWorking(e.target.checked)} style={{ marginTop: 2 }} />
        <span><strong>Already working</strong> — make them active in scheduling and payroll now; the bundle still goes out and shows as unsigned until they sign. Replaces Quick Add.</span>
      </label>

      <DateFlagsBox flags={dateFlags} />
      <MissingDocsBox staff={staff} missing={missing} loaded={docsLoaded} legacy={legacy} busy={docActionBusy} lang={lang} onRequest={onRequestDocs} onUpload={onUploadDoc} />

      {docs.map(d => (
        <DocPreview key={d.doc_type} title={d.doc_label} html={d.content} editing={!!editing[d.doc_type]}
          onToggleEdit={() => setEditing(prev => ({ ...prev, [d.doc_type]: !prev[d.doc_type] }))}
          onChangeHtml={html => setOverrides(prev => ({ ...prev, [d.doc_type]: html }))} />
      ))}

      {docs.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>
          Sending countersigns these {docs.length} documents as <strong>{officeName || 'you'}</strong>, on behalf of A Heart For Care — recorded with a timestamp, same as the candidate's signature.
        </div>
      )}

      {isAgency && (
        <div style={{ fontSize: 11.5, color: 'var(--text2)', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 11px', marginBottom: 8, lineHeight: 1.5 }}>
          {selectedAgency?.name || 'The agency'} normally creates this link from their own portal and sends it
          to {staff.first_name || 'their nurse'} themselves — that's the point of the agency track, so the
          request reaches her from her employer. Use the button below only when they've asked us to do it.
          One document: the Placement Acknowledgment &amp; GAPP Policy Attestation.
        </div>
      )}

      <button style={{ ...btnStyle('var(--success)'), marginTop: 4, width: '100%' }}
        onClick={() => onSend({
          roleCode, track, contractingAgencyId: isAgency ? contractingAgencyId : null, docs,
          payRate: payRate !== '' ? Number(payRate) : null,
          payType: role?.payUnit === 'weekly' ? 'weekly' : 'hourly',
          startDate: startDate || null, acceptByDate: acceptByDate || null,
          candidateName: candidateName || null, preferredLanguage: lang, alreadyWorking,
        })}
        disabled={busy || !canSend}>
        {busy ? 'Sending…'
          : isAgency ? 'Save details & create the signing link'
          : `Countersign & Send ${docs.length || ''} Document${docs.length === 1 ? '' : 's'} for Signature`}
      </button>
      {!isAgency && role && track && !canSend && (() => {
        const needs = [
          !candidateName.trim() && 'candidate name',
          !offerTemplateExists && 'an offer letter template for this role',
          payRate === '' && 'pay rate',
          !startDate && 'start date',
        ].filter(Boolean);
        return needs.length > 0 && (
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6, textAlign: 'center' }}>
            Still needed: {needs.join(', ')}.
          </div>
        );
      })()}
    </div>
  );
}

function LangToggle({ lang, setLang }) {
  return (
    <div style={{ display: 'inline-flex', background: 'var(--bg2)', borderRadius: 8, padding: 3 }}>
      {['en', 'es'].map(l => (
        <button key={l} type="button" onClick={() => setLang(l)} style={{
          padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
          fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
          background: lang === l ? 'var(--navy)' : 'transparent',
          color: lang === l ? '#fff' : 'var(--text2)',
        }}>
          {l === 'en' ? '🇺🇸 English' : '🇲🇽 Español'}
        </button>
      ))}
    </div>
  );
}

// Credential dates that need a look — expired CPR or license, a TB test over
// 12 months old, or a TB "test date" in the future (usually the due date
// typed by mistake). The application form warns the applicant but lets them
// submit (2026-10-16); this is where the reviewer sees it. Never blocks.
function DateFlagsBox({ flags, compact }) {
  if (!flags || flags.length === 0) return null;
  return (
    <div style={{ fontSize: 11, color: 'var(--text)', background: 'var(--warn-bg)', border: '1px solid var(--warn-border)', borderRadius: 8, padding: compact ? '6px 8px' : '8px 10px', marginBottom: 10 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>⚠ {flags.length} document date{flags.length === 1 ? '' : 's'} to check</div>
      <ul style={{ margin: 0, paddingLeft: 16, lineHeight: 1.45 }}>
        {flags.map(f => <li key={f.field}>{flagMessage(f, 'reviewer')}</li>)}
      </ul>
    </div>
  );
}
const appLicenseRequired = (app) => ['rn', 'lpn'].includes(app?.position_applied_for);
const applicationDateFlags = (app) => credentialDateFlags(app || {}, { licenseRequired: appLicenseRequired(app) });

// Required-document gate. Green when everything is on file; otherwise lists
// the gaps with an upload control per gap (for files the office received by
// email/text) and one button that emails the candidate the whole list.
function MissingDocsBox({ staff, missing, loaded, legacy, busy, lang, onRequest, onUpload }) {
  const [dates, setDates] = useState({});
  if (!loaded) return <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>Checking documents on file…</div>;
  if (missing.length === 0) {
    return <div style={{ fontSize: 11, color: 'var(--success)', background: 'var(--success-bg)', border: '1px solid var(--success)', borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>✓ Resume, ID, CPR card, TB result{licenseRequiredFor(staff.position_code, staff.hire_role_code) ? ', license' : ''} all on file.</div>;
  }
  const tone = legacy ? 'var(--warn)' : 'var(--danger)';
  const bg = legacy ? 'var(--warn-bg)' : 'var(--danger-bg)';
  return (
    <div style={{ fontSize: 11, color: tone, background: bg, border: `1px solid ${tone}`, borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        {legacy
          ? `Applied before ${format(new Date(`${DOCUMENT_GATE_SINCE}T00:00:00`), 'MMM d')} — ${missing.length} document${missing.length === 1 ? '' : 's'} not collected then. You can still send; the gaps stay on their follow-up list:`
          : `Cannot send yet — ${missing.length} required document${missing.length === 1 ? '' : 's'} missing:`}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {missing.map(d => (
          <div key={d.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--text)' }}>{d.label}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {d.certType && (
                <input type="date" value={dates[d.key] || ''} onChange={e => setDates(prev => ({ ...prev, [d.key]: e.target.value }))}
                  title={d.dateKind === 'expiry' ? 'Expiry date on the document' : 'Date of the test (valid 12 months)'}
                  style={{ ...inputStyle, width: 'auto', padding: '3px 6px', fontSize: 11 }} />
              )}
              <label style={{ fontSize: 11, color: d.certType && !dates[d.key] ? 'var(--text3)' : 'var(--accent2)', cursor: busy ? 'default' : 'pointer', whiteSpace: 'nowrap' }}
                title={d.certType && !dates[d.key] ? (d.dateKind === 'expiry' ? 'Enter the expiry date first' : 'Enter the test date first') : ''}>
                {busy ? 'Working…' : 'Upload file'}
                <input type="file" disabled={busy || (d.certType && !dates[d.key])} style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(d, f, dates[d.key]); e.target.value = ''; }} />
              </label>
            </span>
          </div>
        ))}
      </div>
      <button type="button" disabled={busy || !staff.email} onClick={() => onRequest(missing.map(d => d.label), lang)}
        style={{ ...btnStyle(tone), width: '100%', marginTop: 8, fontSize: 12 }}>
        {staff.email ? `Email ${staff.first_name || 'candidate'} for the missing documents` : 'No email on file — add one to request documents'}
      </button>
    </div>
  );
}

// ── Screening AHFC performs itself ──────────────────────────────────────────
// Separate from BackgroundCheckPanel on purpose. That panel tracks the
// fingerprint/licence determination for the individual; this one tracks the
// two searches AHFC runs as the enrolled Medicaid provider and never
// delegates to a sending agency. Exclusion screening is required monthly for
// employees AND contractors, so it shows its age rather than just a tick.
function ScreeningPanel({ staff: s, busy, onRecord }) {
  const row = (kind, label, sub, href, linkLabel) => {
    const at = s[SCREENING_KINDS[kind].atField];
    const by = s[SCREENING_KINDS[kind].byField];
    const days = at ? Math.floor((Date.now() - new Date(at)) / 86_400_000) : null;
    const stale = kind === 'exclusion' && days != null && days > 31;
    return (
      <div key={kind} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderTop: '1px solid var(--border)' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: stale ? 'var(--danger)' : 'var(--text)' }}>
            {at ? (stale ? '⚠ ' : '✓ ') : ''}{label}
          </div>
          <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 2, lineHeight: 1.4 }}>
            {at
              ? `${format(new Date(at), 'MMM d, yyyy')}${by ? ` · ${by}` : ''}${stale ? ` · ${days} days ago — due again` : ''}`
              : sub}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
          <a href={href} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm" style={{ textDecoration: 'none', fontSize: 11 }}>{linkLabel} ↗</a>
          <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} disabled={busy} onClick={() => onRecord(kind)}>
            {at ? 'Re-record' : 'Record'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 600 }}>Screening we run ourselves</div>
      <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 2 }}>
        Never delegated to a sending agency — we are the enrolled provider.
      </div>
      {row('registry', 'Registry check', 'Nurse Aide Registry + Georgia Sex Offender Registry', 'https://state.sor.gbi.ga.gov/sort_public/', 'GBI registry')}
      {row('exclusion', 'Exclusion screening', 'GA OIG + federal LEIE + SAM — required monthly', 'https://exclusions.oig.hhs.gov/', 'LEIE')}
    </div>
  );
}

// ── The § 609.5 packet owed to DCH ──────────────────────────────────────────
// GAPP §§ 609.5 / 615.6 / 901.17.2 all say the same thing: for every newly
// engaged nurse the enrolled provider sends these five items to the GAPP
// Compliance Specialist. This is why agency paperwork cannot simply live at
// the agency — we are the ones who have to forward it.
const DCH_EMAIL = 'GAPP.Inquiries@dch.ga.gov';
const DCH_ITEMS = [
  { key: 'resume', label: 'Résumé showing medically-fragile experience', cite: '§ 609.3' },
  { key: 'license_cert', label: 'Current Georgia nursing license', cite: '§ 609.5' },
  { key: 'cpr_card', label: 'Current CPR certification', cite: '§ 609.6' },
  { key: 'background', label: 'Background check / license verification', cite: '§ 615.6', derived: true },
  { key: 'placement_acknowledgment', label: 'Signed GAPP manual attestation', cite: '§ 609.5', mustBeSigned: true },
];

function DchPacketPanel({ staff: s, busy, onMarkSent }) {
  const [docs, setDocs] = useState(null);
  useEffect(() => {
    let active = true;
    supabase.from('staff_documents').select('doc_type, status, pdf_url').eq('staff_id', s.id)
      .then(({ data }) => { if (active) setDocs(data || []); });
    return () => { active = false; };
  }, [s.id]);

  if (!docs) return null;

  const stateOf = (item) => {
    if (item.derived) return s.bg_check_status === 'cleared';
    const d = docs.find(x => x.doc_type === item.key);
    if (!d) return false;
    return item.mustBeSigned ? d.status === 'signed' : (d.status === 'on_file' || d.status === 'signed');
  };
  const urlOf = (key) => docs.find(x => x.doc_type === key)?.pdf_url || null;

  const ready = DCH_ITEMS.every(stateOf);
  const body = [
    `Please find the required documentation for a newly engaged GAPP nurse.`,
    ``,
    `Provider: Hard Shoulder Group LLC d/b/a A Heart For Care`,
    `Medicaid Provider Number: 003331500A · NPI: 1831081991 · COS 971`,
    ``,
    `Nurse: ${s.full_name}${s.position_code ? ` (${s.position_code})` : ''}`,
    s.employment_type === 'agency' ? `Placed by contracted staffing agency under Policy A-2.4.` : ``,
    ``,
    `Attached, per Part II §§ 609.5 / 615.6 / 901.17.2:`,
    ...DCH_ITEMS.map(i => `  ${stateOf(i) ? '-' : '- [MISSING]'} ${i.label} (${i.cite})`),
  ].filter(l => l !== undefined).join('\n');

  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 600 }}>DCH packet</div>
        {s.dch_packet_sent_at && (
          <div style={{ fontSize: 10.5, color: 'var(--success)' }}>
            Sent {format(new Date(s.dch_packet_sent_at), 'MMM d, yyyy')}{s.dch_packet_sent_by ? ` · ${s.dch_packet_sent_by}` : ''}
          </div>
        )}
      </div>
      <div style={{ fontSize: 10.5, color: 'var(--text3)', margin: '2px 0 6px' }}>
        Owed to {DCH_EMAIL} before this nurse takes a GAPP case.
      </div>
      {DCH_ITEMS.map(i => {
        const ok = stateOf(i);
        const url = !i.derived && urlOf(i.key);
        return (
          <div key={i.key} style={{ fontSize: 11.5, padding: '3px 0', color: ok ? 'var(--text2)' : 'var(--warn)' }}>
            {ok ? '✓' : '○'} {url ? <a href={url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent2)' }}>{i.label}</a> : i.label}
            <span style={{ color: 'var(--text3)', fontSize: 10 }}> {i.cite}</span>
          </div>
        );
      })}
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <a
          href={`mailto:${DCH_EMAIL}?subject=${encodeURIComponent(`GAPP newly engaged nurse — ${s.full_name} — A Heart For Care (003331500A)`)}&body=${encodeURIComponent(body)}`}
          className="btn btn-outline btn-sm" style={{ flex: 1, textAlign: 'center', textDecoration: 'none' }}>
          Draft the email
        </a>
        <button className="btn btn-outline btn-sm" style={{ flex: 1 }} disabled={busy} onClick={onMarkSent}>
          {s.dch_packet_sent_at ? 'Re-record as sent' : 'Mark packet sent'}
        </button>
      </div>
      {!ready && (
        <div style={{ fontSize: 10.5, color: 'var(--warn)', marginTop: 6 }}>
          Attach the files yourself — the links above open each one. Items marked ○ aren't on file yet.
        </div>
      )}
    </div>
  );
}

// Always visible regardless of onboarding_status lane — background check runs
// in parallel and must never block the rest of the pipeline (Policy A-2.1's
// 30-day provisional-start allowance is exactly why).
function BackgroundCheckPanel({ staff: s, activeStaffOptions, provisionalSupervisorId, setProvisionalSupervisorId, busy, onStart, onClear, onFlag, onVerifyLicense }) {
  const type = s.bg_check_type || bgCheckTypeFor(s);
  const box = (bg, border) => ({ background: bg, border: `1px solid ${border}`, borderRadius: 8, padding: '10px 12px', marginBottom: 16 });

  // Gate: you legally need signed consent before submitting anyone's info for
  // a background/license check — the consent now signs together with the
  // offer bundle (see sendOffer), so this checks staff_documents directly
  // rather than assuming it's done just because they're past 'candidate'.
  // Only the GCHEXS path consults this now — licence verification is a public
  // lookup and was ungated on 2026-09-28. Agency placements never sign a
  // consent (v56), so they read as satisfied here.
  const consentNotRequired = s.employment_type === 'agency';
  const [consentSigned, setConsentSigned] = useState(null);
  useEffect(() => {
    let active = true;
    if (consentNotRequired) { setConsentSigned(true); return () => { active = false; }; }
    supabase.from('staff_documents').select('status').eq('staff_id', s.id).eq('doc_type', 'background_check_consent').maybeSingle()
      .then(({ data }) => { if (active) setConsentSigned(data?.status === 'signed'); });
    return () => { active = false; };
  }, [s.id, consentNotRequired]);

  if (type === 'license_verification') {
    if (s.bg_check_status === 'cleared') {
      return <div style={box('var(--success-bg)', 'var(--success)')}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--success)' }}>✓ License Verified</div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>RN/LPN — exempt from GCHEXS under Georgia SB 406 (Policy A-2.1).</div>
      </div>;
    }
    return <div style={box('var(--bg2)', 'var(--border)')}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>License Verification</div>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>RN/LPN are exempt from GCHEXS — verify the license instead.</div>
      {/* No consent gate here (2026-09-28). Looking a licence up on Nursys is a
          public primary-source check, not a consumer report, so it needs no
          FCRA authorisation — and the profile's copy of this panel never had
          the gate, so the same action was blocked here and allowed there.
          The GCHEXS path below keeps its gate: that one submits the person's
          details to the state. */}
      <a href="https://www.nursys.com/LQC/LQCTerms.aspx" target="_blank" rel="noreferrer" style={{ ...btnStyle('var(--accent)'), display: 'block', textAlign: 'center', textDecoration: 'none', marginBottom: 8 }}>Verify on Nursys ↗</a>
      <button style={{ ...btnStyle('var(--success)'), width: '100%' }} onClick={onVerifyLicense} disabled={busy}>Mark Verified</button>
    </div>;
  }

  // gchexs path
  if (s.bg_check_status === 'cleared') {
    return <div style={box('var(--success-bg)', 'var(--success)')}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--success)' }}>✓ GCHEXS Cleared</div>
    </div>;
  }
  if (s.bg_check_status === 'flagged') {
    return <div style={box('var(--danger-bg)', 'var(--danger)')}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--danger)' }}>⚠ FLAGGED — Unsatisfactory Determination</div>
      <div style={{ fontSize: 11, color: 'var(--text3)', margin: '4px 0 8px' }}>Per Policy A-2.1: do not assign client care. Review before proceeding.</div>
      <button style={{ ...btnStyle('var(--success)'), width: '100%' }} onClick={onClear} disabled={busy}>Mark Cleared (correction)</button>
    </div>;
  }
  if (s.bg_check_status === 'pending') {
    const left = daysUntil(provisionalExpiry(s.bg_check_initiated_at));
    const supervisor = activeStaffOptions.find(a => a.id === s.provisional_supervisor_id);
    return <div style={box(left != null && left <= 5 ? 'var(--danger-bg)' : 'var(--warn-bg)', left != null && left <= 5 ? 'var(--danger)' : 'var(--warn)')}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--warn)' }}>GCHEXS Pending — Provisional Start</div>
      <div style={{ fontSize: 11, color: 'var(--text3)', margin: '4px 0 8px' }}>
        {left != null ? (left > 0 ? `${left} day${left === 1 ? '' : 's'} left in the 30-day provisional window.` : 'Provisional window has expired.') : ''}
        {' '}{supervisor ? `Supervised by ${supervisor.full_name}.` : 'No provisional supervisor documented yet.'}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button style={{ ...btnStyle('var(--success)'), flex: 1 }} onClick={onClear} disabled={busy}>Mark Cleared</button>
        <button style={{ ...btnStyle('var(--danger)'), flex: 1 }} onClick={onFlag} disabled={busy}>Mark Flagged</button>
      </div>
    </div>;
  }
  // not_started
  return <div style={box('var(--bg2)', 'var(--border)')}>
    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>GCHEXS Background Check</div>
    <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>
      Office initiates the GCHEXS application (SOP-HR01); can run in parallel with the rest of onboarding.
      A 30-day provisional start under direct supervision is allowed while pending (Policy A-2.1).
    </div>
    {consentSigned === false && (
      <div style={{ fontSize: 11, color: 'var(--warn)', marginBottom: 8 }}>Awaiting signed Background Check Consent — required before submitting the GCHEXS application.</div>
    )}
    <select value={provisionalSupervisorId} onChange={e => setProvisionalSupervisorId(e.target.value)} style={{ ...inputStyle, marginBottom: 8 }}>
      <option value="">Provisional supervisor (optional, can add later)</option>
      {activeStaffOptions.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
    </select>
    <button style={{ ...btnStyle('var(--accent)'), width: '100%' }} onClick={onStart} disabled={busy || !consentSigned}>Mark Application Submitted</button>
  </div>;
}


// CLINICAL_CERT_TYPES, needsClinicalCerts, StatusPill and ClinicalCertsPanel
// live in src/components/hr/StaffCompliancePanels.js (2026-08-08), imported
// above — same components, shared with PersonnelProfile.js's Certifications tab.
function Card({ onClick, color, children }) {
  return (
    <div onClick={onClick}
      style={{ background: 'var(--pipeline-card-bg)', border: '1px solid var(--border)', borderRadius: 9, padding: '10px 12px', cursor: 'pointer', transition: 'all 0.15s', boxShadow: 'var(--shadow-pipeline-card)' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = color; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}>
      {children}
    </div>
  );
}

const modalHeader = (title, sub, onClose, extra) => (
  <div style={{ padding: '18px 22px', borderBottom: '0.5px solid var(--border)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
    <div>
      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{title}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{sub}</div>}
      {extra}
    </div>
    <button style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: 'var(--text3)', lineHeight: 1 }} onClick={onClose}>✕</button>
  </div>
);

const btnStyle = (bg) => ({ padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', border: 'none', background: bg, color: '#fff' });
const inputStyle = { width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box' };

export default function OnboardingPipeline() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [staffRows, setStaffRows] = useState([]);
  const [activeGraduates, setActiveGraduates] = useState([]);
  const [activeStaffOptions, setActiveStaffOptions] = useState([]);
  const [invites, setInvites] = useState([]);
  const [appliedEmails, setAppliedEmails] = useState(new Set());
  const [selectedInvite, setSelectedInvite] = useState(null);
  const [inviteActionBusy, setInviteActionBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [busy, setBusy] = useState(false);
  const { toast, showToast, hideToast } = useToast();

  const [inviteModal, setInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteLang, setInviteLang] = useState('en');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteError, setInviteError] = useState('');

  const [docsInfo, setDocsInfo] = useState({ loaded: false, application: null, documents: [] });
  const [docActionBusy, setDocActionBusy] = useState(false);
  const [provisionalSupervisorId, setProvisionalSupervisorId] = useState('');
  const [offerDoc, setOfferDoc] = useState(null);
  const [offerReminderBusy, setOfferReminderBusy] = useState(false);
  const [editingOffer, setEditingOffer] = useState(false);
  // Agency placements are never emailed to the nurse (v56) — the link is shown
  // here so the office can pass it to the agency if they've asked us to.
  const [agencyLink, setAgencyLink] = useState(null);
  const [contractingAgencies, setContractingAgencies] = useState([]);


  const load = useCallback(async () => {
    setLoading(true);
    const [appsRes, staffRes, gradRes, activeRes, inviteRes, allAppEmailsRes, allStaffEmailsRes, agenciesRes] = await Promise.all([
      // Received ones, and ones the Careers form saved whose documents never all
      // arrived — those are on record so the office can call the applicant.
      supabase.from('staff_applications').select('*').in('status', [APPLICATION_RECEIVED, APPLICATION_INCOMPLETE]).order('created_at'),
      supabase.from('staff').select('*').not('onboarding_status', 'in', '("active","withdrawn")').eq('hidden', false).order('created_at'),
      supabase.from('staff').select('*').eq('onboarding_status', 'active').not('application_id', 'is', null).order('updated_at', { ascending: false }).limit(10),
      supabase.from('staff').select('id, full_name').eq('status', 'active').order('full_name'),
      supabase.from('applicant_invites').select('*').order('invited_at', { ascending: false }),
      // Every application ever submitted (any status), not just pending —
      // "has this invite been fulfilled" checks against all of these.
      supabase.from('staff_applications').select('email'),
      supabase.from('staff').select('email'),
      supabase.from('contracting_agencies').select('*').order('name'),
    ]);
    setApplications(appsRes.data || []);
    setStaffRows(staffRes.data || []);
    setActiveGraduates(gradRes.data || []);
    setActiveStaffOptions(activeRes.data || []);
    setInvites(inviteRes.data || []);
    setContractingAgencies(agenciesRes.data || []);
    setAppliedEmails(new Set([
      ...(allAppEmailsRes.data || []).map(a => a.email?.toLowerCase()),
      ...(allStaffEmailsRes.data || []).map(s => s.email?.toLowerCase()),
    ].filter(Boolean)));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setProvisionalSupervisorId(selectedStaff?.provisional_supervisor_id || '');
    setOfferDoc(null);
    setDocsInfo({ loaded: false, application: null, documents: [] });
  }, [selectedStaff]);

  // Required-document gate data: what the application already carries plus
  // anything the office uploaded since (staff_documents).
  const loadDocsInfo = useCallback(async (staff) => {
    if (!staff?.id) return;
    const [{ data: documents }, appRes, { data: certs }] = await Promise.all([
      supabase.from('staff_documents').select('doc_type, status, pdf_url').eq('staff_id', staff.id),
      staff.application_id
        ? supabase.from('staff_applications').select('resume_url, id_document_url, cpr_card_url, tb_result_url, license_cert_url, certification_urls, ssn_last4, cpr_expiry_date, tb_test_date, license_expiry_date').eq('id', staff.application_id).maybeSingle()
        : Promise.resolve({ data: null }),
      supabase.from('staff_certifications').select('cert_type, issued_date, expiry_date').eq('staff_id', staff.id),
    ]);
    setDocsInfo({ loaded: true, application: appRes.data || null, documents: documents || [], certs: certs || [] });
  }, []);
  useEffect(() => { if (selectedStaff?.onboarding_status === 'candidate') loadDocsInfo(selectedStaff); }, [selectedStaff, loadDocsInfo]);

  // The live certification record wins over what the application said —
  // once the office files a current card in Training & Compliance, the flag
  // from the old one goes away. Latest date per type.
  const candidateDateFlags = useMemo(() => {
    if (!selectedStaff || !docsInfo.loaded) return [];
    const latest = (type, col) => (docsInfo.certs || []).filter(c => c.cert_type === type && c[col]).map(c => c[col]).sort().pop();
    const app = docsInfo.application || {};
    const doc = (k) => REQUIRED_DOCS.find(d => d.key === k);
    const dates = {
      cpr_expiry_date: latest(doc('cpr_card').certType, 'expiry_date') || app.cpr_expiry_date,
      tb_test_date: latest(doc('tb_result').certType, 'issued_date') || app.tb_test_date,
      license_expiry_date: latest(doc('license_cert').certType, 'expiry_date') || app.license_expiry_date,
    };
    return credentialDateFlags(dates, { licenseRequired: licenseRequiredFor(selectedStaff.position_code, selectedStaff.hire_role_code) });
  }, [selectedStaff, docsInfo]);

  const missingDocsFor = (roleCode) => {
    if (!selectedStaff) return [];
    return REQUIRED_DOCS.filter(d => {
      if (d.licenseOnly && !licenseRequiredFor(selectedStaff.position_code, roleCode)) return false;
      const inApp = !!docsInfo.application?.[d.appField]
        || (d.key === 'license_cert' && (docsInfo.application?.certification_urls || []).length > 0);
      const inDocs = docsInfo.documents.some(x => x.doc_type === d.key || (d.key === 'license_cert' && x.doc_type === 'certification_upload'));
      return !inApp && !inDocs;
    });
  };

  // Offer bundle (offer letter + job description + background check consent)
  // reminder state — all three share one sign_token/sent_at from sendOffer,
  // so any one row's reminder_sent_at represents the whole bundle.
  useEffect(() => {
    if (!selectedStaff?.id || selectedStaff.onboarding_status !== 'offer_sent') return;
    let active = true;
    supabase.from('staff_documents').select('sign_token, reminder_sent_at')
      .eq('staff_id', selectedStaff.id).eq('status', 'sent')
      .in('doc_type', BUNDLE_DOC_TYPES)
      .limit(1).maybeSingle()
      .then(({ data }) => { if (active) setOfferDoc(data || null); });
    return () => { active = false; };
  }, [selectedStaff?.id, selectedStaff?.onboarding_status]);


  const logEvent = async (staffId, eventType, detail, previousStatus, newStatus) => {
    await supabase.from('onboarding_events').insert({
      staff_id: staffId, event_type: eventType, event_detail: detail,
      previous_status: previousStatus, new_status: newStatus, actor: profile?.full_name || 'system',
    });
  };

  // ── Background check (parallel, non-blocking — see Policy A-2.1) ───────────
  // The 4 handlers below delegate their actual DB mutation to
  // src/lib/staffCompliance.js (2026-08-08) — same logic, shared with
  // PersonnelProfile.js's self-contained BackgroundCheckPanel — while
  // keeping this page's own local state sync (selectedStaff/load/toast)
  // exactly as it was.
  const startBackgroundCheck = async () => {
    setBusy(true);
    try {
      const patch = await startGchexsCheck(selectedStaff.id, provisionalSupervisorId, logEvent);
      showToast('Background check marked as started.');
      load();
      setSelectedStaff(prev => prev ? { ...prev, ...patch } : prev);
    } catch (err) { showToast('Failed: ' + err.message, 'error'); }
    finally { setBusy(false); }
  };

  const clearBackgroundCheck = async () => {
    setBusy(true);
    try {
      const patch = await clearBgCheckShared(selectedStaff, logEvent);
      showToast('Background check marked as cleared.');
      setSelectedStaff(prev => prev ? { ...prev, ...patch } : prev);
      load();
    } catch (err) { showToast('Failed: ' + err.message, 'error'); }
    finally { setBusy(false); }
  };

  const flagBackgroundCheck = async () => {
    if (!window.confirm('Mark this background check as flagged/unsatisfactory? Per Policy A-2.1 this candidate should not be assigned client care.')) return;
    setBusy(true);
    try {
      const patch = await flagBgCheckShared(selectedStaff.id, logEvent);
      showToast('Background check flagged.', 'error');
      setSelectedStaff(prev => prev ? { ...prev, ...patch } : prev);
      load();
    } catch (err) { showToast('Failed: ' + err.message, 'error'); }
    finally { setBusy(false); }
  };

  const markLicenseVerified = async () => {
    setBusy(true);
    try {
      const patch = await markLicenseVerifiedShared(selectedStaff.id, logEvent, profile?.full_name);
      showToast('License marked as verified.');
      setSelectedStaff(prev => prev ? { ...prev, ...patch } : prev);
      load();
    } catch (err) { showToast('Failed: ' + err.message, 'error'); }
    finally { setBusy(false); }
  };

  // Registry and exclusion searches are free public lookups the office runs in
  // a browser; CAERA records that they happened and who did them, which is the
  // part an audit asks for. Deliberately not automated — a scraped result we
  // couldn't stand behind would be worse than a person confirming they looked.
  const recordScreening = async (kind) => {
    setBusy(true);
    try {
      const patch = await recordScreeningCheck(selectedStaff.id, kind, profile?.full_name, logEvent);
      showToast(`${SCREENING_KINDS[kind].label} recorded.`);
      setSelectedStaff(prev => prev ? { ...prev, ...patch } : prev);
      load();
    } catch (err) { showToast('Failed: ' + err.message, 'error'); }
    finally { setBusy(false); }
  };

  const markDchPacketSent = async () => {
    setBusy(true);
    try {
      const patch = { dch_packet_sent_at: new Date().toISOString(), dch_packet_sent_by: profile?.full_name || null };
      const { error } = await supabase.from('staff').update(patch).eq('id', selectedStaff.id);
      if (error) throw error;
      await logEvent(selectedStaff.id, 'dch_packet_sent', `GAPP § 609.5 packet sent to ${DCH_EMAIL}`, null, null);
      setSelectedStaff(prev => prev ? { ...prev, ...patch } : prev);
      showToast('Recorded as sent to DCH.');
      load();
    } catch (err) { showToast('Failed: ' + err.message, 'error'); }
    finally { setBusy(false); }
  };

  // ── Approve an agency placement (v56) ───────────────────────────────────────
  // The explicit review step that replaces auto-activation for this track.
  // Approving is what writes the office countersignature onto the signed
  // acknowledgment — before this, nobody here had looked at a file the agency
  // assembled, so a countersignature stamped at send time asserted something
  // that had not happened.
  const approveAgencyPlacement = async () => {
    if (!window.confirm(
      `Approve ${selectedStaff.full_name}?\n\nThis countersigns her acknowledgment as ${profile?.full_name || 'you'}, makes her active in CAERA, creates her AxisCare account and sends her welcome email.\n\nConfirm you have checked her license verification, registry and exclusion screening, and the documents her agency uploaded.`
    )) return;
    setBusy(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/submit-staff-signatures`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'approve', accessToken: sess?.session?.access_token, staffId: selectedStaff.id }),
      }).then(r => r.json());
      if (!res?.success) throw new Error(res?.error || 'Approval failed');
      showToast(`${selectedStaff.full_name} approved and activated.`);
      setSelectedStaff(null);
      load();
    } catch (err) { showToast('Failed: ' + err.message, 'error'); }
    finally { setBusy(false); }
  };

  // ── Invite a prospective applicant ──────────────────────────────────────────
  // Points at the public /careers/apply form directly (no per-invite token —
  // unlike offer/onboarding sign links, the application form itself isn't
  // gated per-candidate, so there's nothing to track/expire here).
  const sendApplicantInvite = async () => {
    const email = inviteEmail.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setInviteError('Enter a valid email address.'); return; }
    setInviteSending(true); setInviteError('');
    try {
      await notifyApplicantInvite(email, inviteName.trim(), `${window.location.origin}/careers/apply`, inviteLang);
      await supabase.from('applicant_invites').insert({
        email: email.toLowerCase(), name: inviteName.trim() || null, language: inviteLang,
        invited_by_id: profile?.id || null, invited_by_name: profile?.full_name || null,
      });
      setInviteModal(false);
      setInviteEmail(''); setInviteName(''); setInviteLang('en');
      showToast(`Invitation sent to ${email}.`);
      load();
    } catch (err) { setInviteError('Failed to send invite: ' + err.message); }
    finally { setInviteSending(false); }
  };

  // ── Invite follow-up: resend or dismiss a still-pending invite ──────────────
  const resendInvite = async (invite) => {
    setInviteActionBusy(true);
    try {
      await notifyApplicantInvite(invite.email, invite.name, `${window.location.origin}/careers/apply`, invite.language);
      const { error } = await supabase.from('applicant_invites').update({ reminder_sent_at: new Date().toISOString() }).eq('id', invite.id);
      if (error) throw error;
      showToast(`Reminder sent to ${invite.email}.`);
      setSelectedInvite(null);
      load();
    } catch (err) { showToast('Failed to resend: ' + err.message, 'error'); }
    finally { setInviteActionBusy(false); }
  };

  const removeInvite = async (invite) => {
    if (!window.confirm(`Remove this invite record for ${invite.email}? This can't be undone.`)) return;
    setInviteActionBusy(true);
    try {
      const { error } = await supabase.from('applicant_invites').delete().eq('id', invite.id);
      if (error) throw error;
      setSelectedInvite(null);
      showToast('Invite removed.');
      load();
    } catch (err) { showToast('Failed: ' + err.message, 'error'); }
    finally { setInviteActionBusy(false); }
  };

  // ── Application review ────────────────────────────────────────────────────
  // Public form now asks RN/LPN/PCA directly (2026-07-27), so this is a
  // straight pass-through for those three. "office_admin" and "other" are
  // deliberately left unassigned — HR determines the real back-office role
  // (PCC/DIR_OPS/BOC/Manager) manually, matching the earlier decision to
  // never expose those on the public form. PCA's family-vs-independent
  // caregiver_relationship is also left for HR to set post-hire, not asked
  // of applicants.
  const POSITION_APPLIED_FOR_TO_ROLE = { rn: 'RN', lpn: 'LPN', pca: 'PCA' };
  const convertApplication = async (app) => {
    // Soft duplicate check — warn, don't block (mirrors Personnel.js
    // handleSave). Added 2026-09-07 after a fast-tracked nurse was
    // quick-added on day one (no email on the record) and then converted
    // again from her application the next day, leaving two staff rows.
    const q = v => `"${String(v).replace(/"/g, '')}"`;
    const orFilters = [
      `and(first_name.ilike.${q(app.first_name || '')},last_name.ilike.${q(app.last_name || '')})`,
      app.email ? `email.ilike.${q(app.email)}` : null,
      app.phone ? `phone.eq.${q(app.phone)}` : null,
    ].filter(Boolean).join(',');
    const { data: possibleDupes } = await supabase
      .from('staff').select('employee_id, full_name, position_code, status, onboarding_status, axiscare_id')
      .or(orFilters).eq('hidden', false);
    if (possibleDupes?.length) {
      const list = possibleDupes.map(s => `${s.employee_id || '—'} ${s.full_name} — ${s.position_code || 'no role'}, ${s.status}${s.axiscare_id ? `, AxisCare ID ${s.axiscare_id}` : ''}`).join('\n');
      const proceed = window.confirm(
        `A staff record matching "${app.first_name} ${app.last_name}" already exists:\n\n${list}\n\nConverting will create a second, separate record. If this is the same person, cancel and attach the application details to the existing record instead.\n\nCreate a new record anyway?`
      );
      if (!proceed) return;
    }
    setBusy(true);
    try {
      const positionCode = POSITION_APPLIED_FOR_TO_ROLE[app.position_applied_for] || null;
      const track = ['employee', 'contractor', 'agency'].includes(app.employment_type_desired) ? app.employment_type_desired : 'employee';
      const { data: newStaff, error } = await supabase.from('staff').insert([{
        full_name: `${app.first_name} ${app.last_name}`.trim(),
        first_name: app.first_name || null, last_name: app.last_name || null,
        email: app.email, phone: app.phone,
        address: app.address || null, city: app.city || null, state: app.state || 'GA', zip: app.zip || null,
        date_of_birth: app.date_of_birth,
        position_code: positionCode,
        employment_type: track, hire_track: track,
        ssn_last4: app.ssn_last4 || null,
        status: 'inactive', onboarding_status: 'candidate', source: 'application', application_id: app.id,
        bg_check_type: bgCheckTypeFor({ position_code: positionCode }),
        preferred_language: app.form_language === 'es' ? 'es' : 'en',
        notes: app.work_history,
      }]).select().single();
      if (error) throw error;
      const { error: appUpdateErr } = await supabase.from('staff_applications').update({
        status: 'converted', reviewed_by: profile?.full_name, reviewed_at: new Date().toISOString(), converted_staff_id: newStaff.id,
      }).eq('id', app.id);
      if (appUpdateErr) throw appUpdateErr;
      await logEvent(newStaff.id, 'candidate_created', `Converted from job application (${track})`, null, 'candidate');

      // Everything the applicant uploaded becomes a Documents-tab row (Drive
      // files already, no signing) — and CPR/TB/license also become
      // certification rows so Training & Compliance tracks their expiry.
      const uploadRows = [];
      const certRows = [];
      if (app.resume_url) uploadRows.push({ staff_id: newStaff.id, doc_type: 'resume', doc_label: 'Resume', status: 'on_file', pdf_url: app.resume_url, sent_at: app.created_at });
      if (app.id_document_url) uploadRows.push({ staff_id: newStaff.id, doc_type: 'id_document', doc_label: 'Government ID', status: 'on_file', pdf_url: app.id_document_url, sent_at: app.created_at });
      REQUIRED_DOCS.filter(d => d.certType && app[d.appField]).forEach(d => {
        uploadRows.push({ staff_id: newStaff.id, doc_type: d.key, doc_label: d.label, status: 'on_file', pdf_url: app[d.appField], sent_at: app.created_at });
        const dates = certDatesFor(d, app[d.dateField]);
        certRows.push({ staff_id: newStaff.id, cert_type: d.certType, cert_label: d.certLabel, status: 'active', verified: false, document_url: app[d.appField], ...dates, notes: dates.expiry_date ? 'Uploaded with the application — verify against the document.' : 'Uploaded with the application — verify and enter the expiry date.' });
      });
      (app.certification_urls || []).forEach(c => uploadRows.push({ staff_id: newStaff.id, doc_type: 'certification_upload', doc_label: c.label || 'Certification', status: 'on_file', pdf_url: c.url, sent_at: app.created_at }));
      if (app.signed_pdf_url) uploadRows.push({
        staff_id: newStaff.id, doc_type: 'application', doc_label: 'Employment Application',
        status: 'signed', pdf_url: app.signed_pdf_url,
        signed_name: app.consent_signature_name, signed_at: app.consent_signed_at,
      });
      if (uploadRows.length > 0) {
        const { error: docsErr } = await supabase.from('staff_documents').insert(uploadRows);
        if (docsErr) throw docsErr;
      }
      if (certRows.length > 0) {
        const { error: certErr } = await supabase.from('staff_certifications').insert(certRows);
        if (certErr) console.error('certification rows from application failed (non-blocking):', certErr);
      }
      setSelectedApp(null);
      showToast(`${newStaff.full_name} added as a candidate.`);
      load();
    } catch (err) { showToast('Failed to convert: ' + err.message, 'error'); }
    finally { setBusy(false); }
  };

  const rejectApplication = async (app) => {
    if (!window.confirm(`Reject ${app.first_name} ${app.last_name}'s application? This can't be undone.`)) return;
    setBusy(true);
    try {
      const { error } = await supabase.from('staff_applications').update({
        status: 'rejected', reviewed_by: profile?.full_name, reviewed_at: new Date().toISOString(),
      }).eq('id', app.id);
      if (error) throw error;
      setSelectedApp(null);
      showToast('Application rejected.');
      try {
        await notifyApplicationRejected(app.email, `${app.first_name} ${app.last_name}`.trim(), app.form_language);
      } catch (emailErr) {
        console.error('Rejection email failed (non-blocking):', emailErr);
      }
      load();
    } catch (err) { showToast('Failed: ' + err.message, 'error'); }
    finally { setBusy(false); }
  };

  // ── Offer letter + job description + background check consent ──────────────
  // Role/employment-type selection happens here (office prepares the offer),
  // not on the public application form — see onboardingTemplates.js.
  // 2026-07-22: Background Check Consent now bundles into this same signing
  // action instead of a separate "Compliance" stage (see submit-staff-
  // signatures — signing all of these jumps straight to Gusto & AxisCare
  // Setup, no more manual "send onboarding docs" step in between).
  // ── The one gate: prepare and send the bundle ───────────────────────────
  // Every document signs under one token. The office's identity is captured
  // as the countersignature at send time. Track/role/pay/dates typed here
  // are persisted to the staff record so Personnel, Payroll and AxisCare all
  // read the same values. "Already working" makes the person active now
  // (scheduling + payroll) while the bundle stays out for signature.
  const sendBundle = async ({ roleCode, track, contractingAgencyId, docs, payRate, payType, startDate, acceptByDate, candidateName, preferredLanguage, alreadyWorking }) => {
    const isAgencyTrack = track === 'agency';
    if (!isAgencyTrack && !docs?.length) { showToast('Nothing to send — pick a role first.', 'error'); return; }
    if (isAgencyTrack && !contractingAgencyId) { showToast('Select a contracting agency first.', 'error'); return; }
    if (!selectedStaff.email) { showToast('This candidate has no email on file — add one before sending.', 'error'); return; }
    setBusy(true);
    try {
      const token = crypto.randomUUID();
      const now = new Date().toISOString();
      // Any of the pack's documents already out unsigned on its own — sent
      // from the Personnel checklist, say — is replaced by the pack: one
      // unsigned copy per type is all the database allows, and the whole
      // pack used to fail on it (Bolatito Adenuga, 2026-10-19: an offer
      // letter sent alone from Personnel blocked all six). Put back if the
      // pack itself cannot be saved.
      let replaced = [];
      if (!isAgencyTrack) {
        const countersign = { office_signed_by: profile?.full_name || null, office_signed_at: now, office_signer_role: profile?.role || null };
        const { data: earlier, error: earlierErr } = await supabase.from('staff_documents').select('id, doc_label')
          .eq('staff_id', selectedStaff.id).eq('status', 'sent').in('doc_type', docs.map(d => d.doc_type));
        if (earlierErr) throw earlierErr;
        replaced = earlier || [];
        if (replaced.length) {
          const { error: wErr } = await supabase.from('staff_documents').update({ status: 'withdrawn', updated_at: now }).in('id', replaced.map(r => r.id));
          if (wErr) throw wErr;
        }
        const rows = docs.map(d => ({ staff_id: selectedStaff.id, doc_type: d.doc_type, doc_label: d.doc_label, content: d.content, sign_token: token, status: 'sent', sent_at: now, ...countersign }));
        const { error } = await supabase.from('staff_documents').insert(rows);
        if (error) {
          if (replaced.length) await supabase.from('staff_documents').update({ status: 'sent', updated_at: now }).in('id', replaced.map(r => r.id));
          throw error;
        }
      }

      const resolvedPosition = TEMPLATE_CODE_TO_POSITION[roleCode] || {};
      const nameSplit = candidateName ? splitFullName(candidateName) : null;
      const expiresAt = acceptByDate ? new Date(`${acceptByDate}T23:59:59`).toISOString() : null;
      const { error: statusErr } = await supabase.from('staff').update({
        // The agency path leaves the lane alone here — agency-request-signature
        // moves it to offer_sent once the document actually exists, so a failed
        // call can't strand someone in a lane with nothing out for signature.
        ...(isAgencyTrack ? {} : { onboarding_status: 'offer_sent', offer_sent_at: now }),
        offer_expires_at: expiresAt, withdrawn_at: null,
        hire_role_code: roleCode, hire_track: track, employment_type: track,
        contracting_agency_id: track === 'agency' ? contractingAgencyId : null,
        ...resolvedPosition,
        bg_check_type: bgCheckTypeFor({ position_code: resolvedPosition.position_code || selectedStaff.position_code }),
        ...(candidateName ? { full_name: candidateName, first_name: nameSplit.first, last_name: nameSplit.last } : {}),
        ...(payRate != null ? { pay_rate: payRate, pay_type: payType } : {}),
        ...(startDate ? { start_date: startDate } : {}),
        ...(preferredLanguage ? { preferred_language: preferredLanguage } : {}),
        ...(alreadyWorking ? { status: 'active', fast_tracked_at: selectedStaff.fast_tracked_at || now } : {}),
      }).eq('id', selectedStaff.id);
      if (statusErr) throw statusErr;

      const trackLabel = TRACKS.find(t => t.key === track)?.label || track;

      // ── Agency track: build the document server-side, email nobody ────────
      // The nurse hears about this from her own employer. We hand the office a
      // copyable link for the case where the agency has asked us to pass it on.
      if (isAgencyTrack) {
        const { data: sess } = await supabase.auth.getSession();
        const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/agency-request-signature`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ accessToken: sess?.session?.access_token, staffId: selectedStaff.id }),
        }).then(r => r.json());
        if (!res?.success) {
          throw new Error(res?.error === 'already_signed'
            ? 'Already signed — nothing to send.'
            : 'Could not create the signing link.');
        }
        const agencyUrl = `${window.location.origin}${res.signLink}`;
        setAgencyLink({ name: candidateName || selectedStaff.full_name, url: agencyUrl });
        showToast(res.reused
          ? 'A signing link was already outstanding — showing that one.'
          : 'Signing link created. No email was sent.');
        load();
        return;
      }

      await logEvent(selectedStaff.id, 'offer_sent', `${docs.map(d => d.doc_label).join(', ')} sent (${getRole(roleCode)?.label || roleCode}, ${trackLabel})${alreadyWorking ? ' — already working, made active now' : ''}${expiresAt ? `, withdraws after ${format(new Date(expiresAt), 'MMM d')}` : ''}`, 'candidate', 'offer_sent');
      const link = `${window.location.origin}/hr-sign/${token}`;
      await notifyOfferLetterReady(selectedStaff.email, candidateName || selectedStaff.full_name, link);
      const replacedNote = replaced.length ? ` ${replaced.map(r => r.doc_label).join(', ')} sent on ${replaced.length === 1 ? 'its' : 'their'} own earlier ${replaced.length === 1 ? 'was' : 'were'} replaced — that link no longer works.` : '';
      if (replaced.length) await logEvent(selectedStaff.id, 'note', `${replaced.map(r => r.doc_label).join(', ')} already out for signature on ${replaced.length === 1 ? 'its' : 'their'} own replaced by the offer pack; the earlier link no longer works.`, null, null);
      setSelectedStaff(null);
      showToast(`${docs.length} documents sent to ${candidateName || selectedStaff.full_name}.${replacedNote}`);
      load();
    } catch (err) { showToast('Failed to send: ' + err.message, 'error'); }
    finally { setBusy(false); }
  };

  // Withdrawn offers drop off the board. A fast-tracked person who is
  // already working keeps status=active (they are on the schedule); the
  // office decides separately whether to end that engagement.
  const withdrawOffer = async () => {
    if (!window.confirm(`Withdraw the unsigned offer for ${selectedStaff.full_name}? Their signing link will stop working.`)) return;
    setBusy(true);
    try {
      const now = new Date().toISOString();
      const { error: docErr } = await supabase.from('staff_documents').update({ status: 'withdrawn', updated_at: now })
        .eq('staff_id', selectedStaff.id).eq('status', 'sent').in('doc_type', BUNDLE_DOC_TYPES);
      if (docErr) throw docErr;
      const { error } = await supabase.from('staff').update({
        onboarding_status: 'withdrawn', withdrawn_at: now, offer_expires_at: null,
        ...(selectedStaff.fast_tracked_at ? {} : { status: 'inactive' }),
      }).eq('id', selectedStaff.id);
      if (error) throw error;
      await logEvent(selectedStaff.id, 'offer_withdrawn', `Offer withdrawn by ${profile?.full_name || 'office'}`, 'offer_sent', 'withdrawn');
      // Someone pushed to AxisCare ahead of signing (Quick Add, or an
      // application converted after a manual push) must not stay Active
      // there when they are inactive here. Best-effort: a failure is logged
      // on the timeline, never blocks the withdrawal.
      let axisNote = '';
      if (selectedStaff.axiscare_id && !selectedStaff.fast_tracked_at) {
        const { data: axisData, error: axisErr } = await updateCaregiver(selectedStaff.axiscare_id, { status: 'Inactive' });
        const failed = axisErr || axisData?.errors?.length;
        await logEvent(selectedStaff.id, failed ? 'axiscare_status_failed' : 'axiscare_deactivated',
          failed ? `Could not set AxisCare caregiver ${selectedStaff.axiscare_id} to Inactive: ${axisErr?.message || axisData.errors[0]}` : `AxisCare caregiver ${selectedStaff.axiscare_id} set to Inactive`, null, null);
        axisNote = failed ? ' AxisCare could not be updated — set them Inactive there by hand.' : ' AxisCare set to Inactive.';
      }
      setSelectedStaff(null);
      showToast(`Offer withdrawn for ${selectedStaff.full_name}.${axisNote}`, axisNote.includes('could not') ? 'error' : 'success');
      load();
    } catch (err) { showToast('Failed: ' + err.message, 'error'); }
    finally { setBusy(false); }
  };

  const requestMissingDocs = async (labels, lang) => {
    setDocActionBusy(true);
    try {
      await notifyApplicationDocumentsMissing(selectedStaff.email, selectedStaff.full_name, labels, lang || selectedStaff.preferred_language);
      await logEvent(selectedStaff.id, 'documents_requested', `Asked for: ${labels.join(', ')}`, null, null);
      showToast(`Asked ${selectedStaff.full_name} for ${labels.length} document${labels.length === 1 ? '' : 's'}.`);
    } catch (err) { showToast('Failed: ' + err.message, 'error'); }
    finally { setDocActionBusy(false); }
  };

  // A file the office received by email/text — filed to the candidate's
  // Drive folder through the same function the public form uses, recorded
  // as a Documents-tab row and (CPR/TB/license) a certification row.
  const uploadMissingDoc = async (doc, file, dateValue) => {
    setDocActionBusy(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = ev => resolve(ev.target.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { data, error: upErr } = await supabase.functions.invoke('upload-staff-document', {
        body: { fileBase64: base64, filename: file.name, applicantName: selectedStaff.full_name, contentType: file.type || 'application/octet-stream' },
      });
      if (upErr || !data?.success) throw new Error(data?.error || upErr?.message || 'Upload failed');
      const now = new Date().toISOString();
      const { error: docErr } = await supabase.from('staff_documents').insert([{ staff_id: selectedStaff.id, doc_type: doc.key, doc_label: doc.label, status: 'on_file', pdf_url: data.url, sent_at: now }]);
      if (docErr) throw docErr;
      if (doc.certType) {
        const dates = certDatesFor(doc, dateValue);
        await supabase.from('staff_certifications').insert([{ staff_id: selectedStaff.id, cert_type: doc.certType, cert_label: doc.certLabel, status: 'active', verified: false, document_url: data.url, ...dates, notes: dates.expiry_date ? 'Uploaded by the office — verify against the document.' : 'Uploaded by the office — verify and enter the expiry date.' }]);
      }
      await logEvent(selectedStaff.id, 'document_uploaded', `${doc.label} uploaded by ${profile?.full_name || 'office'}`, null, null);
      await loadDocsInfo(selectedStaff);
      showToast(`${doc.label} on file.`);
    } catch (err) { showToast('Upload failed: ' + err.message, 'error'); }
    finally { setDocActionBusy(false); }
  };

  const sendOfferReminder = async () => {
    if (!offerDoc?.sign_token) return;
    setOfferReminderBusy(true);
    try {
      const link = `${window.location.origin}/hr-sign/${offerDoc.sign_token}`;
      const agencyPlacement = selectedStaff.hire_track === 'agency';
      if (agencyPlacement) {
        const agencyName = contractingAgencies.find(a => a.id === selectedStaff.contracting_agency_id)?.name;
        await notifyPlacementAcknowledgmentReminder(selectedStaff.email, selectedStaff.full_name, agencyName, link);
      } else {
        await notifyOfferLetterReminder(selectedStaff.email, selectedStaff.full_name, link);
      }
      const now = new Date().toISOString();
      const { error } = await supabase.from('staff_documents').update({ reminder_sent_at: now })
        .eq('staff_id', selectedStaff.id).eq('sign_token', offerDoc.sign_token);
      if (error) throw error;
      await logEvent(selectedStaff.id, 'offer_reminder_sent', agencyPlacement ? `Placement Acknowledgment reminder emailed to ${selectedStaff.email}` : 'Reminder sent for offer/job description/background check consent signature', null, null);
      setOfferDoc(prev => prev ? { ...prev, reminder_sent_at: now } : prev);
      showToast(`Reminder emailed to ${selectedStaff.email}.`);
    } catch (err) { showToast('Failed to send reminder: ' + err.message, 'error'); }
    finally { setOfferReminderBusy(false); }
  };

  // The office route sends no email: the link is handed to the agency. It can
  // be shown again at any time — the function returns the outstanding link
  // rather than minting a second one.
  const showAgencyLink = async () => {
    setBusy(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/agency-request-signature`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken: sess?.session?.access_token, staffId: selectedStaff.id }),
      }).then(r => r.json());
      if (!res?.success) throw new Error(res?.error === 'already_signed' ? 'Already signed.' : 'Could not fetch the signing link.');
      setAgencyLink({ name: selectedStaff.full_name, url: `${window.location.origin}${res.signLink}` });
    } catch (err) { showToast(err.message, 'error'); }
    finally { setBusy(false); }
  };

  const rowsForLane = (laneKey) => staffRows.filter(s => s.onboarding_status === laneKey);
  // Invited but not yet applied — an invite "graduates" out of this lane the
  // moment a matching email shows up anywhere in staff_applications/staff,
  // computed live rather than tracked with a link that could go stale.
  const pendingInvites = invites.filter(i => !appliedEmails.has((i.email || '').toLowerCase()));

  if (loading) return <div className="empty-state"><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 12, color: 'var(--text3)' }}>
          {pendingInvites.length} invited · {applications.length} new application{applications.length !== 1 ? 's' : ''} · {staffRows.length} in progress
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <a href="/careers/apply" target="_blank" rel="noreferrer"
            style={{ ...btnStyle('var(--navy)'), borderRadius: 20, textDecoration: 'none', display: 'inline-block' }}>
            + New Application
          </a>
          <button style={{ ...btnStyle('var(--accent)'), borderRadius: 20 }} onClick={() => setInviteModal(true)}>
            Invite New Applicant
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 12, flex: 1 }}>
        {/* Invited — not yet applied */}
        <div style={{ background: 'var(--topbar-bg)', border: '1.5px solid #0EA5E9', borderRadius: 12, minWidth: 220, flex: 0.8, display: 'flex', flexDirection: 'column' }}>
          <LaneHeader label="Invited" color="#0EA5E9" count={pendingInvites.length} />
          <div style={{ padding: '6px 10px 10px', flex: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 290px)', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pendingInvites.length === 0 && <EmptyLane color="#0EA5E9" />}
            {pendingInvites.map(inv => {
              const days = Math.floor((Date.now() - new Date(inv.invited_at).getTime()) / 86400000);
              return (
                <Card key={inv.id} onClick={() => setSelectedInvite(inv)} color="#0EA5E9">
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{inv.name || inv.email}</div>
                  {inv.name && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{inv.email}</div>}
                  <div style={{ fontSize: 9, color: days > 14 ? 'var(--danger)' : days > 5 ? '#D97706' : 'var(--text3)', marginTop: 6 }}>
                    {days > 14 ? `⏱ Stale — invited ${days}d ago` : `Invited ${days === 0 ? 'today' : `${days}d ago`}`}{inv.reminder_sent_at ? ' · reminded' : ''}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Received Applications lane */}
        <div style={{ background: 'var(--topbar-bg)', border: '1.5px solid var(--border2)', borderRadius: 12, minWidth: 240, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <LaneHeader label="Received Applications" color="var(--text3)" count={applications.length} />
          <div style={{ padding: '6px 10px 10px', flex: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 290px)', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {applications.length === 0 && <EmptyLane color="var(--text3)" />}
            {applications.map(app => (
              <Card key={app.id} onClick={() => setSelectedApp(app)} color="var(--text3)">
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{app.last_name}, {app.first_name}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2, textTransform: 'capitalize' }}>{app.position_applied_for}</div>
                <div style={{ fontSize: 9, color: 'var(--text3)', marginTop: 6 }}>{app.status === APPLICATION_INCOMPLETE ? 'Started' : 'Applied'} {format(new Date(app.created_at), 'MMM d')}</div>
                {app.status === APPLICATION_INCOMPLETE && (
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--danger)', background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 4, padding: '1px 6px', marginTop: 6, display: 'inline-block' }}>
                    Incomplete · missing {missingDocuments(app).map(d => d.label).join(', ') || 'nothing (not finished)'}
                  </div>
                )}
                {applicationDateFlags(app).length > 0 && (
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text)', background: 'var(--warn-bg)', border: '1px solid var(--warn-border)', borderRadius: 4, padding: '1px 6px', marginTop: 6, display: 'inline-block' }}>
                    ⚠ {applicationDateFlags(app).length} date{applicationDateFlags(app).length === 1 ? '' : 's'} to check
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>

        {LANES.map(lane => {
          const rows = rowsForLane(lane.key);
          return (
            <div key={lane.key} style={{ background: 'var(--topbar-bg)', border: '0.5px solid var(--border)', borderRadius: 12, minWidth: 240, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <LaneHeader label={lane.label} color={lane.color} count={rows.length} />
              <div style={{ padding: '6px 10px 10px', flex: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 290px)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {rows.length === 0 && <EmptyLane color={lane.color} />}
                {rows.map(s => (
                  <Card key={s.id} onClick={() => setSelectedStaff(s)} color={lane.color}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{s.last_name ? `${s.last_name}, ${s.first_name}` : s.full_name}</div>
                      {s.employment_type === 'agency' && (
                        <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--copper)', background: 'var(--copper-bg)', borderRadius: 4, padding: '1px 5px' }}>AGENCY</span>
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: lane.color, marginTop: 2 }}>{s.position_code ? (POSITION_LABELS[s.position_code] || s.position_code) + (s.is_lead ? ' (Lead)' : '') : 'pending'}</div>
                    {s.onboarding_status === 'offer_sent' && s.offer_sent_at && (() => {
                      const days = Math.floor((Date.now() - new Date(s.offer_sent_at).getTime()) / 86400000);
                      return (
                        <div style={{ fontSize: 9, color: days > 5 ? 'var(--warn)' : 'var(--text3)', marginTop: 4 }}>
                          Sent {days === 0 ? 'today' : `${days}d ago`}{s.offer_expires_at ? ` · expires ${format(new Date(s.offer_expires_at), 'MMM d')}` : ''}{s.fast_tracked_at ? ' · ⚡ working' : ''}
                        </div>
                      );
                    })()}
                    <BgCheckBadge staff={s} />
                  </Card>
                ))}
              </div>
            </div>
          );
        })}

        {/* Recently active — informational */}
        <div style={{ background: 'var(--topbar-bg)', border: '1.5px solid var(--success)', borderRadius: 12, minWidth: 200, flex: 0.7, display: 'flex', flexDirection: 'column' }}>
          <LaneHeader label="Recently Active" color="var(--success)" count={activeGraduates.length} />
          <div style={{ padding: '6px 10px 10px', flex: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 290px)', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {activeGraduates.length === 0 && <EmptyLane color="var(--success)" />}
            {activeGraduates.map(s => (
              <div key={s.id} onClick={() => navigate(`/hr/personnel/${s.id}`)} title="Open their profile"
                style={{ background: 'var(--pipeline-card-bg)', border: '1px solid var(--border)', borderRadius: 9, padding: '10px 12px', cursor: 'pointer', boxShadow: 'var(--shadow-pipeline-card)' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{s.last_name ? `${s.last_name}, ${s.first_name}` : s.full_name}</div>
                <div style={{ fontSize: 10, color: 'var(--success)', marginTop: 2 }}>✓ Active</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Application detail modal */}
      {selectedApp && (
        <ModalBackdrop onClose={() => setSelectedApp(null)}>
          {modalHeader(`${selectedApp.last_name}, ${selectedApp.first_name}`, `Applied for ${selectedApp.position_applied_for}`, () => setSelectedApp(null))}
          <div style={{ padding: '14px 22px', display: 'flex', flexDirection: 'column', gap: 0 }}>
            {selectedApp.status === APPLICATION_INCOMPLETE && (
              <div style={{ marginBottom: 10, padding: '10px 12px', borderRadius: 8, background: 'var(--danger-bg)', border: '1px solid var(--danger)', fontSize: 12, color: 'var(--text)', lineHeight: 1.5 }}>
                <b>Not finished.</b> The applicant pressed Submit on {format(new Date(selectedApp.created_at), 'MMM d, h:mm a')}, but {missingDocuments(selectedApp).length
                  ? <>these documents never arrived: <b>{missingDocuments(selectedApp).map(d => d.label).join(', ')}</b></>
                  : 'the application was never completed'}.
                {' '}Call them on {selectedApp.phone || 'the number above'}: from the same phone they can finish it within {APPLICATION_OPEN_HOURS} hours of starting, and what they typed is kept. After that, ask them to apply again.
              </div>
            )}
            <DateFlagsBox flags={applicationDateFlags(selectedApp)} />
            {[
              ['Email', selectedApp.email],
              ['Phone', selectedApp.phone || '—'],
              ['Address', selectedApp.address || '—'],
              ['Employment type desired', (selectedApp.employment_type_desired || '—').replace('_', ' ')],
              ['Resume', selectedApp.resume_url ? <a href={selectedApp.resume_url} target="_blank" rel="noreferrer">View</a> : '—'],
              ['ID document', selectedApp.id_document_url ? <a href={selectedApp.id_document_url} target="_blank" rel="noreferrer">View</a> : '—'],
              ['CPR / First Aid card', selectedApp.cpr_card_url ? <span><a href={selectedApp.cpr_card_url} target="_blank" rel="noreferrer">View</a>{selectedApp.cpr_expiry_date && <span style={{ color: 'var(--text3)' }}> · expires {selectedApp.cpr_expiry_date}</span>}</span> : <span style={{ color: 'var(--danger)' }}>Missing</span>],
              ['TB test result', selectedApp.tb_result_url ? <span><a href={selectedApp.tb_result_url} target="_blank" rel="noreferrer">View</a>{selectedApp.tb_test_date && <span style={{ color: 'var(--text3)' }}> · tested {selectedApp.tb_test_date}</span>}</span> : <span style={{ color: 'var(--danger)' }}>Missing</span>],
              ['Nursing license / certificate', selectedApp.license_cert_url ? <span><a href={selectedApp.license_cert_url} target="_blank" rel="noreferrer">View</a>{selectedApp.license_expiry_date && <span style={{ color: 'var(--text3)' }}> · expires {selectedApp.license_expiry_date}</span>}</span> : (licenseRequired(selectedApp.position_applied_for) ? <span style={{ color: 'var(--danger)' }}>Missing</span> : '—')],
              ['SSN', selectedApp.ssn_last4 ? `••• •• ${selectedApp.ssn_last4}` : <span style={{ color: 'var(--danger)' }}>Not provided</span>],
              ['Certifications', (selectedApp.certification_urls || []).length > 0
                ? (selectedApp.certification_urls || []).map((c, i) => <span key={i}><a href={c.url} target="_blank" rel="noreferrer">{c.label}</a>{i < selectedApp.certification_urls.length - 1 ? ', ' : ''}</span>)
                : '—'],
              ['Signed', selectedApp.consent_signature_name ? `${selectedApp.consent_signature_name} — ${format(new Date(selectedApp.consent_signed_at), 'MMM d, yyyy')}` : '—'],
            ].map(([label, val]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '7px 0', borderBottom: '0.5px solid var(--border)' }}>
                <span style={{ fontSize: 11, color: 'var(--text3)', flexShrink: 0, marginRight: 12 }}>{label}</span>
                <span style={{ fontSize: 12, color: 'var(--text)', fontWeight: 500, textAlign: 'right' }}>{val}</span>
              </div>
            ))}
            {selectedApp.work_history && (
              <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text2)', background: 'var(--bg2)', borderRadius: 7, padding: '8px 10px', lineHeight: 1.5 }}>
                {selectedApp.work_history}
              </div>
            )}
          </div>
          <div style={{ padding: '12px 22px', borderTop: '0.5px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn btn-outline" onClick={() => rejectApplication(selectedApp)} disabled={busy}>Reject</button>
            <button style={btnStyle('var(--success)')} onClick={() => convertApplication(selectedApp)} disabled={busy}>{busy ? 'Working…' : 'Convert to Candidate →'}</button>
          </div>
        </ModalBackdrop>
      )}

      {/* Staff stage detail modal */}
      {selectedStaff && (
        <ModalBackdrop onClose={() => setSelectedStaff(null)}>
          {modalHeader(selectedStaff.last_name ? `${selectedStaff.last_name}, ${selectedStaff.first_name}` : selectedStaff.full_name, LANES.find(l => l.key === selectedStaff.onboarding_status)?.label || selectedStaff.onboarding_status, () => { setSelectedStaff(null); setAgencyLink(null); },
            <div style={{ marginTop: 4 }}>
              <a href="#" onClick={e => { e.preventDefault(); navigate(`/hr/personnel/${selectedStaff.id}`); }} style={{ fontSize: 11, color: 'var(--accent2)' }}>
                View Full Profile (documents, certifications, timeline) →
              </a>
            </div>
          )}
          <div style={{ padding: '18px 22px' }}>

            {agencyLink && (
              <div style={{ background: 'var(--success-bg)', border: '1px solid var(--success)', borderRadius: 8, padding: '10px 12px', marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--success)', marginBottom: 4 }}>
                  Signing link ready for {agencyLink.name}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, lineHeight: 1.5 }}>
                  No email was sent. Pass this to the agency so it reaches her from her own employer.
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input readOnly value={agencyLink.url} onFocus={e => e.target.select()}
                    style={{ ...inputStyle, flex: 1, fontFamily: 'monospace', fontSize: 11 }} />
                  <button style={btnStyle('var(--accent)')}
                    onClick={() => navigator.clipboard?.writeText(agencyLink.url).then(() => showToast('Link copied.'))}>
                    Copy
                  </button>
                </div>
              </div>
            )}

            <BackgroundCheckPanel
              staff={selectedStaff}
              activeStaffOptions={activeStaffOptions}
              provisionalSupervisorId={provisionalSupervisorId}
              setProvisionalSupervisorId={setProvisionalSupervisorId}
              busy={busy}
              onStart={startBackgroundCheck}
              onClear={clearBackgroundCheck}
              onFlag={flagBackgroundCheck}
              onVerifyLicense={markLicenseVerified}
            />

            <ClinicalCertsPanel staff={selectedStaff} />

            <ScreeningPanel staff={selectedStaff} busy={busy} onRecord={recordScreening} />

            <DchPacketPanel staff={selectedStaff} busy={busy} onMarkSent={markDchPacketSent} />

            {selectedStaff.onboarding_status === 'candidate' && (
              <OfferPanel staff={selectedStaff} agencies={contractingAgencies} busy={busy} onSend={sendBundle} officeName={profile?.full_name}
                missingFor={missingDocsFor} dateFlags={candidateDateFlags} docsLoaded={docsInfo.loaded} docActionBusy={docActionBusy}
                onRequestDocs={requestMissingDocs} onUploadDoc={uploadMissingDoc} />
            )}

            {selectedStaff.onboarding_status === 'pending_review' && (
              <div style={{ background: 'var(--warn-bg)', border: '1px solid var(--warn)', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--warn)', marginBottom: 4 }}>
                  Signed — awaiting your review
                </div>
                <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.55, marginBottom: 10 }}>
                  {selectedStaff.first_name || 'She'} signed the Placement Acknowledgment &amp; GAPP Policy Attestation
                  {selectedStaff.offer_signed_at ? ` on ${format(new Date(selectedStaff.offer_signed_at), 'MMM d, yyyy')}` : ''}.
                  Her agency assembled this file, so nobody here has approved it yet — she is not active, has no
                  AxisCare account and has had no welcome email. Check the screening above and the documents on
                  her profile, then approve.
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>
                  Approving countersigns her acknowledgment as <strong>{profile?.full_name || 'you'}</strong>, with a timestamp.
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button style={{ ...btnStyle('var(--success)'), flex: 1 }} onClick={approveAgencyPlacement} disabled={busy}>
                    {busy ? 'Approving…' : 'Approve & Activate'}
                  </button>
                  <button className="btn btn-outline" style={{ color: 'var(--danger)', borderColor: 'var(--danger-border)' }} onClick={withdrawOffer} disabled={busy}>
                    Reject
                  </button>
                </div>
              </div>
            )}

            {selectedStaff.onboarding_status === 'offer_sent' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 13, color: 'var(--text2)' }}>
                  {selectedStaff.hire_track === 'agency'
                    ? <>⏳ Waiting on {selectedStaff.first_name || 'their'} signature on the Placement Acknowledgment. No email was sent — the link goes to the agency to pass on. Signing moves them to <b>Awaiting Review</b>; approving there makes them active, creates the AxisCare account and sends the welcome email.</>
                    : <>⏳ Waiting on {selectedStaff.first_name || 'their'} signature. Signing makes them active, creates their AxisCare account and sends the welcome email automatically.</>}
                </div>
                {selectedStaff.hire_track === 'agency' && !agencyLink && (
                  <button className="btn btn-outline btn-sm" style={{ alignSelf: 'flex-start' }} onClick={showAgencyLink} disabled={busy}>
                    {busy ? 'Fetching…' : 'Show signing link'}
                  </button>
                )}
                {selectedStaff.fast_tracked_at && selectedStaff.status === 'active' && (
                  <div style={{ fontSize: 11, color: 'var(--warn)', background: 'var(--warn-bg)', border: '1px solid var(--warn)', borderRadius: 8, padding: '8px 10px' }}>
                    ⚡ Already working — active in scheduling and payroll; contract still unsigned.
                  </div>
                )}
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                  Sent {selectedStaff.offer_sent_at ? format(new Date(selectedStaff.offer_sent_at), 'MMM d, yyyy') : '—'}
                  {selectedStaff.offer_expires_at ? ` · withdraws automatically after ${format(new Date(selectedStaff.offer_expires_at), 'MMM d, yyyy')}` : ' · no expiry set'}
                  {offerDoc?.reminder_sent_at ? ` · last reminder ${format(new Date(offerDoc.reminder_sent_at), 'MMM d, h:mm a')}` : ''}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button style={{ ...btnStyle('var(--accent)'), flex: 1 }} onClick={sendOfferReminder} disabled={offerReminderBusy || !offerDoc}>
                    {offerReminderBusy ? 'Sending…' : 'Send Reminder'}
                  </button>
                  {selectedStaff.hire_track !== 'agency' && (
                    <button className="btn btn-outline" onClick={() => setEditingOffer(true)} disabled={busy}>
                      Edit &amp; Resend
                    </button>
                  )}
                  <button className="btn btn-outline" style={{ color: 'var(--danger)', borderColor: 'var(--danger-border)' }} onClick={withdrawOffer} disabled={busy}>
                    Withdraw Offer
                  </button>
                </div>
              </div>
            )}
          </div>
        </ModalBackdrop>
      )}

      {inviteModal && (
        <ModalBackdrop onClose={() => setInviteModal(false)}>
          {modalHeader('Invite New Applicant', 'Sends an email with a link to the public application form', () => setInviteModal(false))}
          <div style={{ padding: '14px 22px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={fieldLabel}>Applicant Email</div>
              <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="name@example.com" style={inputStyle} />
            </div>
            <div>
              <div style={fieldLabel}>Applicant Name (optional)</div>
              <input value={inviteName} onChange={e => setInviteName(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <div style={fieldLabel}>Email Language</div>
              <div style={{ display: 'inline-flex', background: 'var(--bg2)', borderRadius: 8, padding: 3 }}>
                {['en', 'es'].map(l => (
                  <button key={l} type="button" onClick={() => setInviteLang(l)} style={{
                    padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                    fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
                    background: inviteLang === l ? 'var(--navy)' : 'transparent',
                    color: inviteLang === l ? '#fff' : 'var(--text2)',
                  }}>
                    {l === 'en' ? '🇺🇸 English' : '🇲🇽 Español'}
                  </button>
                ))}
              </div>
            </div>
            {inviteError && <div style={{ fontSize: 12, color: 'var(--danger)' }}>{inviteError}</div>}
          </div>
          <div style={{ padding: '12px 22px', borderTop: '0.5px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn btn-outline" onClick={() => setInviteModal(false)}>Cancel</button>
            <button style={btnStyle('var(--accent)')} onClick={sendApplicantInvite} disabled={inviteSending || !inviteEmail.trim()}>
              {inviteSending ? 'Sending…' : 'Send Invite'}
            </button>
          </div>
        </ModalBackdrop>
      )}

      {selectedInvite && (
        <ModalBackdrop onClose={() => setSelectedInvite(null)}>
          {modalHeader(selectedInvite.name || selectedInvite.email, 'Invited, not yet applied', () => setSelectedInvite(null))}
          <div style={{ padding: '14px 22px', display: 'flex', flexDirection: 'column', gap: 0 }}>
            {[
              ['Email', selectedInvite.email],
              ['Language', selectedInvite.language === 'es' ? 'Español' : 'English'],
              ['Invited by', selectedInvite.invited_by_name || '—'],
              ['Invited at', format(new Date(selectedInvite.invited_at), 'MMM d, yyyy h:mm a')],
              ['Last reminder', selectedInvite.reminder_sent_at ? format(new Date(selectedInvite.reminder_sent_at), 'MMM d, yyyy h:mm a') : 'Not yet sent'],
            ].map(([label, val]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '7px 0', borderBottom: '0.5px solid var(--border)' }}>
                <span style={{ fontSize: 11, color: 'var(--text3)', flexShrink: 0, marginRight: 12 }}>{label}</span>
                <span style={{ fontSize: 12, color: 'var(--text)', fontWeight: 500, textAlign: 'right' }}>{val}</span>
              </div>
            ))}
          </div>
          <div style={{ padding: '12px 22px', borderTop: '0.5px solid var(--border)', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <button className="btn btn-outline" onClick={() => removeInvite(selectedInvite)} disabled={inviteActionBusy} style={{ color: 'var(--danger)' }}>Remove</button>
            <button style={btnStyle('var(--accent)')} onClick={() => resendInvite(selectedInvite)} disabled={inviteActionBusy}>
              {inviteActionBusy ? 'Sending…' : 'Resend Invite'}
            </button>
          </div>
        </ModalBackdrop>
      )}

      {editingOffer && selectedStaff && (
        <ReviseOfferModal
          staff={selectedStaff}
          signed={false}
          onClose={() => setEditingOffer(false)}
          onDone={({ axisNote }) => {
            setEditingOffer(false);
            setSelectedStaff(null);
            showToast(`Updated offer letter sent to ${selectedStaff.full_name}.${axisNote ? ` ${axisNote}` : ''}`, axisNote ? 'warn' : 'success');
            load();
          }}
        />
      )}
      <Toast {...(toast || {})} onClose={hideToast} />
    </div>
  );
}
